import { FC } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
  type XAxisTickContentProps,
} from 'recharts';
import type { SopResumoMes } from '@/types/dashboards';
import { produzidoBase } from '@/utils/equiplexSop';
import { escalaDoGrafico, type Grandeza, margemDoRotuloEmPe, type MedidasRotulo } from '@/utils/rotulosGrafico';
import { fmtCompacto, fmtData, fmtDesvio, fmtUn, mesCurto } from '@/utils/painelFormat';
import AreaDoGrafico from '../AreaDoGrafico';
import { ALTURA_GRAFICO, FONTE_ROTULO, FONTE_EIXO, tickEixo, type CoresPainel } from '../chartTheme';
import Legenda from '../Legenda';
import { rotuloNoTopo, type LinhaExtra } from '../rotuloBarra';
import PainelCard from '../PainelCard';
import TooltipBox from '../TooltipBox';

interface Ponto {
  mes: string;
  rotulo: string;
  titulo: string;
  programado: number;
  programadoTotal: number;
  produzido: number;
  produzidoTotal: number;
  desvio: number | null;
  emAndamento: boolean;
  futuro: boolean;
}

interface Props {
  /** O período e os meses de antes dele (o histórico, até 6). */
  resumos: SopResumoMes[];
  /** Os meses do período: o rótulo deles fica em negrito quando o gráfico mostra também o histórico. */
  periodo?: string[];
  /** Último dia fechado: no mês em andamento as duas barras só têm as OPs até aqui. */
  fechadoAte: string;
  cores: CoresPainel;
}

const rotuloMes = (r: SopResumoMes): string => {
  if (r.futuro) return `${mesCurto(r.label)} (futuro)`;
  return r.em_andamento ? `${mesCurto(r.label)}*` : mesCurto(r.label);
};

const nfPct0 = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });

/** Desvio inteiro e com sinal ("−15%"): na barra, curto; com a casa decimal, no tooltip. */
const desvioCurto = (d: number): string => {
  const r = Math.round(d);
  return r === 0 ? '0%' : `${r > 0 ? '+' : '−'}${nfPct0.format(Math.abs(r))}%`;
};

/** O desvio do mês vai numa segunda linha, em cima do número do produzido. */
const desvioAcima = (dados: Ponto[], cores: CoresPainel): LinhaExtra => ({
  texto: (i) => {
    const d = dados[i]?.desvio;
    return d === null || d === undefined ? null : desvioCurto(d);
  },
  cor: cores.textoMuted,
});

const unidadeDaGrandeza = (g: Grandeza): string => (g === 'milhões' ? 'milhões de unidades' : g === 'mil' ? 'mil unidades' : 'unidades');

/** Rótulo do mês no eixo: o mês do período em negrito, o do histórico apagado. */
function tickDoMes(dados: Ponto[], periodo: string[], cores: CoresPainel) {
  const TickDoMes = ({ x, y, payload, index }: XAxisTickContentProps & { index?: number }) => {
    const doPeriodo = periodo.includes(dados[index ?? -1]?.mes ?? '');
    return (
      <text
        x={Number(x)}
        y={Number(y) + 12}
        textAnchor="middle"
        fontSize={FONTE_EIXO}
        fontWeight={doPeriodo ? 700 : 400}
        fill={doPeriodo ? cores.texto : cores.textoMuted}
      >
        {String(payload?.value ?? '')}
      </text>
    );
  };
  return TickDoMes;
}

/** Duas colunas por mês: deitado, ~74 px por mês; em pé, ~44 (03/11/2026). */
const MEDIDAS: MedidasRotulo = { reservado: 72, deitado: 74, emPe: 44 };

/**
 * Programado × produzido por mês. No mês em andamento as duas barras são das OPs
 * com início **até o último dia fechado** (é isso que o desvio compara); os totais
 * do mês aparecem no tooltip. No mês futuro só existe programado. As duas barras mostram o
 * número; o desvio fica em cima do produzido (26/10/2026: sem precisar passar o mouse).
 */
const ProgramadoProduzidoChart: FC<Props> = ({ resumos, periodo, fechadoAte, cores }) => {
  const dados: Ponto[] = resumos.map((r) => ({
    mes: r.mes,
    rotulo: rotuloMes(r),
    titulo: r.label,
    programado: r.futuro ? r.programado : r.programado_base,
    programadoTotal: r.programado,
    produzido: r.futuro ? r.produzido : produzidoBase(r),
    produzidoTotal: r.produzido,
    desvio: r.desvio_pct,
    emAndamento: r.em_andamento,
    futuro: r.futuro,
  }));
  const temAndamento = dados.some((d) => d.emAndamento);
  // Só destaca quando há mês de fora do período no gráfico (o histórico).
  const destaque = periodo && dados.some((d) => !periodo.includes(d.mes)) ? periodo : null;
  // Com até 12 meses de duas barras, "3,2 mi" encavala no vizinho: o número sai sem sufixo.
  const escala = escalaDoGrafico(dados.flatMap((d) => [d.programado, d.produzido]));
  const extra = desvioAcima(dados, cores);
  const topoEmPe = margemDoRotuloEmPe(
    dados.flatMap((d, i) => [escala.formatar(d.programado), `${escala.formatar(d.produzido)}  ${extra.texto(i) ?? ''}`]),
    FONTE_ROTULO,
  );

  const conteudoTooltip = ({ active, payload }: TooltipContentProps) => {
    const p = active && payload?.[0] ? (payload[0].payload as Ponto) : null;
    if (!p) return null;
    const ate = p.emAndamento ? ` até ${fmtData(fechadoAte).slice(0, 5)}` : '';
    const linhas = [
      { rotulo: `Programado${ate}`, valor: fmtUn(p.programado), cor: cores.programado },
      { rotulo: `Produzido${ate}`, valor: fmtUn(p.produzido), cor: cores.produzido },
      { rotulo: 'Desvio', valor: fmtDesvio(p.desvio) },
    ];
    if (p.emAndamento) {
      linhas.push(
        { rotulo: 'Programado no mês', valor: fmtUn(p.programadoTotal) },
        { rotulo: 'Produzido até agora', valor: fmtUn(p.produzidoTotal) },
      );
    }
    return <TooltipBox titulo={p.titulo} linhas={linhas} />;
  };

  return (
    <PainelCard
      testId="sop-chart-volume"
      titulo="Volume: programado × produzido"
      subtitulo={
        `${
          temAndamento
            ? `Unidades por mês de produção. * Mês em andamento: só OPs com início até ${fmtData(fechadoAte)} (dias fechados).`
            : 'Unidades por mês de produção (16 a 15).'
        }${destaque ? ' Os meses do período em negrito; os de antes, para comparar.' : ''}`
      }
    >
      <Legenda
        itens={[
          { rotulo: 'Programado (OPs)', cor: cores.programado },
          { rotulo: 'Produzido (reportado nas OPs)', cor: cores.produzido },
        ]}
      />
      <p className="text-xs text-content-muted">
        Números nas barras em {unidadeDaGrandeza(escala.grandeza)}; em cima do produzido, o desvio contra o programado.
      </p>
      <AreaDoGrafico altura={ALTURA_GRAFICO} categorias={dados.length} medidas={MEDIDAS}>
        {(modo) => (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={dados} margin={{ top: modo === 'em-pe' ? Math.max(topoEmPe, 40) : 40, right: 8, bottom: 0, left: 0 }} barGap={4} barCategoryGap="24%">
              <CartesianGrid vertical={false} stroke={cores.grade} />
              <XAxis
                dataKey="rotulo"
                tick={destaque ? tickDoMes(dados, destaque, cores) : tickEixo(cores)}
                axisLine={{ stroke: cores.eixo }}
                tickLine={false}
              />
              <YAxis
                tickFormatter={fmtCompacto}
                tick={tickEixo(cores)}
                axisLine={false}
                tickLine={false}
                width={64}
              />
              <Tooltip content={conteudoTooltip} cursor={{ fill: cores.grade, fillOpacity: 0.35 }} />
              <Bar dataKey="programado" name="Programado" fill={cores.programado} radius={[4, 4, 0, 0]} maxBarSize={56} isAnimationActive={false}>
                {modo && <LabelList dataKey="programado" content={rotuloNoTopo(escala.formatar, cores.texto, undefined, modo === 'em-pe')} />}
              </Bar>
              <Bar dataKey="produzido" name="Produzido" fill={cores.produzido} radius={[4, 4, 0, 0]} maxBarSize={56} isAnimationActive={false}>
                {modo && <LabelList dataKey="produzido" content={rotuloNoTopo(escala.formatar, cores.texto, extra, modo === 'em-pe')} />}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </AreaDoGrafico>
    </PainelCard>
  );
};

export default ProgramadoProduzidoChart;
